<!-- The shelf — everyone whose recipes you keep. The only tab-bar
     destination in the app; every other screen hangs off a link from here
     or from a cook's book. -->
<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  IonPage, IonHeader, IonToolbar, IonButtons, IonButton, IonContent,
} from '@ionic/vue';
import { useRouter } from 'vue-router';
import {
  cooks, recipes, marginNotes,
  cooksReady, recipesReady, marginNotesReady,
  addCook, saveCook, removeCook,
} from '../store.js';
import { remove as removeWithUndo } from '../core/undo.js';
import HdIcon from '../components/HdIcon.vue';
import HdDialog from '../components/HdDialog.vue';
import HdSheet from '../components/HdSheet.vue';

const router = useRouter();

const RELATIONSHIPS = [
  { value: 'mum', label: 'Mum' },
  { value: 'dad', label: 'Dad' },
  { value: 'nonna', label: 'Nonna' },
  { value: 'nonno', label: 'Nonno' },
  { value: 'grandma', label: 'Grandma' },
  { value: 'grandpa', label: 'Grandpa' },
  { value: 'aunt', label: 'Aunt' },
  { value: 'uncle', label: 'Uncle' },
  { value: 'other', label: 'Other' },
];
const relationshipLabel = (v) => RELATIONSHIPS.find((r) => r.value === v)?.label ?? v;

const loaded = ref(false);
onMounted(async () => {
  await Promise.all([cooksReady, recipesReady, marginNotesReady]);
  loaded.value = true;
});

// Kept recipes only — one still "capturing" hasn't been handed down yet, so
// it shouldn't inflate the count shown on the shelf.
const keptCount = (cookId) =>
  recipes.value.filter((r) => String(r.cookId) === String(cookId) && r.status === 'kept').length;

/* One pass over the notes for the whole shelf, not one pass per card. A note
 * belongs to a recipe, and a recipe belongs to a cook, so the count on a card
 * is two joins deep — done naively that is cooks × recipes × notes on every
 * keystroke of the add form.
 *
 * Only a note with a `mediaUrl` counts. A note typed rather than spoken is
 * kept and shown everywhere else in the app, but the mic on this card promises
 * a voice, and a card that promises a voice it cannot play is a small lie. */
const shelf = computed(() => {
  const cookOfRecipe = new Map(recipes.value.map((r) => [String(r.id), String(r.cookId)]));
  const said = new Map();
  for (const note of marginNotes.value) {
    if (!note.mediaUrl) continue;
    const cookId = cookOfRecipe.get(String(note.recipeId));
    if (cookId === undefined) continue;
    said.set(cookId, (said.get(cookId) ?? 0) + 1);
  }
  return cooks.value.map((cook) => ({
    cook,
    kept: keptCount(cook.id),
    said: said.get(String(cook.id)) ?? 0,
  }));
});

// BY NAME, not by path — the navigation shell decides where a screen is
// actually mounted (bottom-tabs nests every product route under /tabs/), so a
// literal plan path can stop matching once the shell has it. AND WITH THE
// PARAM THE ROUTE ACTUALLY DECLARES: this page used to push
// `{ name: 'capture', params: { id: row.id } }`, which threw
// `Missing required param "recipeId"` on every tap — no route in this app
// names its param `id`, and a row here is a cook, not a recipe. A cook opens
// that cook's book, which is `/cook/:cookId`.
const open = (cook) => router.push({ name: 'cook-book', params: { cookId: cook.id } });
const goKeeping = () => router.push({ name: 'keeping' });

const adding = ref(false);
const name = ref('');
const relationship = ref('mum');
const kitchen = ref('');

function startAdding() {
  name.value = '';
  relationship.value = 'mum';
  kitchen.value = '';
  adding.value = true;
}
function cancelAdding() {
  adding.value = false;
}
function submitAdding() {
  const trimmed = name.value.trim();
  if (!trimmed) return;
  const row = addCook({
    name: trimmed,
    relationship: relationship.value,
    kitchen: kitchen.value.trim(),
  });
  adding.value = false;
  open(row);
}

// ------------------------------------------------------------------ manage
//
// A visible ··· mark beside every book, the same mark the rest of the app
// uses for "what can I do with this". It was press-and-hold with a caption
// under the list; a control that needs explaining is a control nobody finds.

const same = (a, b) => String(a) === String(b);
const recipeCount = (cookId) =>
  recipes.value.filter((r) => same(r.cookId, cookId)).length;

const manageId = ref(null);
const manageCook = computed(
  () => (manageId.value == null ? null : cooks.value.find((c) => same(c.id, manageId.value)) ?? null),
);
function openManage(cook) { manageId.value = cook.id; }

const editing = ref(null);
const editInputs = computed(() => [
  { name: 'name', type: 'text', value: editing.value?.name ?? '', placeholder: 'Name' },
  { name: 'relationship', type: 'select', label: 'Relationship', value: editing.value?.relationship ?? 'other', options: RELATIONSHIPS },
  { name: 'kitchen', type: 'text', value: editing.value?.kitchen ?? '', placeholder: 'Kitchen (optional)' },
]);
const editButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Save',
    handler: (data) => {
      const row = editing.value ? cooks.value.find((c) => same(c.id, editing.value.id)) : null;
      const name = String(data?.name ?? '').trim();
      if (!row || !name) return;
      const rel = RELATIONSHIPS.some((r) => r.value === data?.relationship) ? data.relationship : row.relationship;
      saveCook({ ...row, name, relationship: rel, kitchen: String(data?.kitchen ?? '').trim() });
    },
  },
];

/* A COOK WITH RECIPES IS NOT DELETED FROM HERE. This is a refusal, and it is
 * the safest of the two honest options.
 *
 * Cascading would be one tap on one row that ends with every recording that
 * person ever made erased from IndexedDB — and a recording in this app is
 * usually the only copy of a voice that may not be available to record again.
 * No confirmation sentence is worth that much: "Delete Nonna Rosa? Her four
 * recipes and everything she said in them go too" is read in a second, and the
 * loss is permanent.
 *
 * Deleting the recipes first is not busywork. Each one is its own named,
 * counted confirmation inside her book, so the person sees the four recordings
 * they are spending before they spend them — and can stop after the first.
 * An empty cook, by contrast, owns nothing, cascades nowhere, and deletes with
 * an ordinary undo. */
const blocked = ref(null);
const blockedButtons = computed(() => {
  const cook = blocked.value?.cook;
  return [
    { text: 'Not now', role: 'cancel' },
    { text: 'Open their book', handler: () => { if (cook) open(cook); } },
  ];
});

function askDelete(cook) {
  const n = recipeCount(cook.id);
  if (n > 0) {
    blocked.value = {
      cook,
      header: `${cook.name} is not empty.`,
      message: n === 1
        ? 'One recipe of theirs is still here, and what they said while cooking is inside it. Delete it from their book first, so you see what goes with it.'
        : `${n} recipes of theirs are still here, and what they said while cooking is inside them. Delete them from their book first, one at a time, so you see what each one takes with it.`,
    };
    return;
  }

  /* Off the shelf now, out of storage when the window closes — nothing is
     actually lost until it is too late to change your mind, which is what
     makes Undo real rather than decorative. */
  const shelf = cooks.value.slice();
  cooks.value = cooks.value.filter((c) => !same(c.id, cook.id));
  removeWithUndo({
    text: `${cook.name} taken off the shelf.`,
    commit: () => removeCook(cook.id),
    revert: () => { cooks.value = shelf; },
  });
}

const manageButtons = computed(() => {
  const cook = manageCook.value;
  if (!cook) return [];
  return [
    {
      text: 'Fix the details',
      handler: () => {
        editing.value = { id: cook.id, name: cook.name ?? '', relationship: cook.relationship ?? 'other', kitchen: cook.kitchen ?? '' };
        manageId.value = null;
      },
    },
    {
      text: 'Take off the shelf',
      role: 'destructive',
      handler: () => { askDelete(cook); manageId.value = null; },
    },
    { text: 'Cancel', role: 'cancel' },
  ];
});
</script>

<template>
  <IonPage>
    <IonHeader class="hd-bar">
      <IonToolbar>
        <IonButtons slot="end">
          <!-- Labelled "Settings", not "Keeping", on purpose. A gear icon reads
               as Settings to someone on a screen reader, and it is what the
               store-screenshot runner looks for to reach this screen — it
               matches [aria-label="Settings"], and a relabelled button made the
               settings shots unreachable. The screen it opens is still called
               Keeping; that name lives on the screen, where it is read. -->
          <IonButton aria-label="Settings" title="Settings" @click="goKeeping">
            <HdIcon name="gear" :size="20" />
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent :fullscreen="true" class="hd-content">
      <div v-if="!loaded" class="hd-page waiting" aria-hidden="true"></div>

      <div v-else class="hd-page shelf">
        <h1 class="title">The shelf</h1>
        <p class="subtitle">Everyone whose cooking you keep</p>

        <p v-if="!cooks.length" class="empty">
          No one on the shelf yet. Start with whoever cooks the thing you would miss most.
        </p>

        <ul v-else class="cooks">
          <!-- A COOK IS A BOOK, seen edge-on: the spine carries the same red
               rule the pages inside carry, and the footer says what is in
               there. A bare numeral at the far edge said nothing about which
               of the two things it was counting. -->
          <li v-for="entry in shelf" :key="entry.cook.id" class="book-li">
            <button
              type="button"
              class="hd-card book"
              @click="open(entry.cook)"
              @contextmenu.prevent="openManage(entry.cook)"
            >
              <!-- Feint, not red, when the book is empty: the red in this app
                   is the margin rule of a page somebody actually wrote. -->
              <span class="spine" :class="{ blank: !entry.kept }" aria-hidden="true"></span>

              <span class="who">
                <span class="name">{{ entry.cook.name }}</span>
                <span class="meta">
                  {{ relationshipLabel(entry.cook.relationship) }}<template v-if="entry.cook.kitchen"> · {{ entry.cook.kitchen }}</template>
                </span>

                <span v-if="entry.kept" class="counts">
                  <span class="hd-figure n">{{ entry.kept }}</span>
                  {{ entry.kept === 1 ? 'recipe' : 'recipes' }}
                  <!-- Nothing said, nothing claimed. "0 recordings" beside
                       somebody's name reads as a reproach. -->
                  <template v-if="entry.said">
                    <span class="dot" aria-hidden="true">·</span>
                    <HdIcon name="mic" :size="11" :weight="1.9" class="mic" />
                    <span class="hd-figure n said">{{ entry.said }}</span>
                    <span class="sr-only">{{ entry.said === 1 ? 'recording' : 'recordings' }}</span>
                  </template>
                </span>
                <!-- Not "0 recipes". An empty book is a thing to fill in, and
                     saying so is the only useful thing this line can do. -->
                <span v-else class="counts blank">Nothing captured yet</span>
              </span>

            </button>
            <button
              type="button"
              class="more"
              :aria-label="`What to do with ${entry.cook.name}`"
              @click="openManage(entry.cook)"
            >
              <HdIcon name="dots" :size="18" />
            </button>
          </li>
        </ul>

        <section class="add" :class="{ first: !cooks.length }">
          <button v-if="!adding" type="button" class="add-btn" @click="startAdding">
            <HdIcon name="plus" :size="15" :weight="2" />
            <span>Add a cook</span>
          </button>

          <form v-else class="add-form" @submit.prevent="submitAdding">
            <label class="field">
              <span class="label">Name</span>
              <input v-model="name" type="text" placeholder="Nonna Rosa" autocomplete="off" required />
            </label>

            <label class="field">
              <span class="label">Relationship</span>
              <select v-model="relationship">
                <option v-for="r in RELATIONSHIPS" :key="r.value" :value="r.value">{{ r.label }}</option>
              </select>
            </label>

            <label class="field">
              <span class="label">Kitchen (optional)</span>
              <input v-model="kitchen" type="text" placeholder="Reservoir, since 1971" autocomplete="off" />
            </label>

            <div class="actions">
              <button type="button" class="cancel" @click="cancelAdding">Cancel</button>
              <button type="submit" class="save">Add a cook</button>
            </div>
          </form>
        </section>
      </div>

      <HdSheet
        :open="manageCook !== null"
        :header="manageCook?.name || 'This cook'"
        :buttons="manageButtons"
        @dismiss="manageId = null"
      />

      <HdDialog
        :open="editing !== null"
        header="Fix the details"
        :inputs="editInputs"
        :buttons="editButtons"
        @dismiss="editing = null"
      />

      <!-- Not a confirmation: an explanation of why this one does not happen
           here. It names the count and points at the door. -->
      <HdDialog
        :open="blocked !== null"
        :header="blocked?.header"
        :message="blocked?.message"
        :buttons="blockedButtons"
        @dismiss="blocked = null"
      />
    </IonContent>
  </IonPage>
</template>

<style scoped>
.waiting { min-height: 100%; }
/* The left inset was `--hd-margin-w + 20`, which existed to clear the page's
   red margin rule. With the rule gone that is 76px of empty paper down the
   side of a phone, and the cards were the narrowest thing on the screen for
   no reason anyone could see. Even inset now: the shelf is a list of objects,
   not a page with a margin. */
.shelf { padding: 24px 18px 64px; min-height: 100%; position: relative; }
/* NO PAGE MARGIN RULE ON THIS SCREEN ANY MORE.
 *
 * It was drawn full-height down the left, 20px clear of the cards — and now
 * that every cook wears a red spine, the shelf had two parallel reds saying
 * different things, with the page's one running on past the last card into
 * empty paper. The red is the margin of a RECIPE; a shelf is the outside of
 * the books, not the inside of one. Removed rather than recoloured, because
 * a second vertical line here has no job at all.
 *
 * Deliberately not deleted from the other screens: on the sheet it is the
 * margin the notes live in, which is the whole design. */

.title { margin: 0 0 4px; font-size: var(--hd-t-title); font-weight: 500; line-height: 1.1; letter-spacing: -0.01em; }
.subtitle {
  margin: 0 0 20px;
  color: var(--hd-pencil);
  font-style: italic;
  font-size: var(--hd-t-body);
  line-height: 1.3;
}

/* The empty shelf gets the room a sentence needs to be read, and then the one
   thing to do about it, rather than a line of prose with a control after it. */
.empty {
  max-width: 26em;
  margin: 8px 0 0;
  color: var(--hd-pencil);
  font-style: italic;
  font-size: var(--hd-t-body);
  line-height: var(--hd-rule);
}

.cooks { margin: 0; padding: 0; list-style: none; }
.cooks li + li { margin-top: 10px; }
.book-li { position: relative; }

/* Outside the card's button (a button inside a button is not HTML), sitting
   over its top-right corner. Pencil until pressed. */
.more {
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: none;
  border: 0;
  color: var(--hd-pencil);
  cursor: pointer;
}

/* The whole card is the button — a name with a chevron at the far side that is
   not itself the target is a card that looks tappable and isn't. */
.book {
  display: flex;
  align-items: stretch;
  gap: 14px;
  width: 100%;
  min-height: 44px;
  padding: 14px 44px 14px 0;
  text-align: left;
  cursor: pointer;
  color: var(--hd-ink);
  font-family: inherit;
}

/* Stretched rather than fixed-height: it is the edge of the book, so it runs
   the full depth of the card whatever the name wraps to. */
.spine {
  flex: none;
  width: 4px;
  align-self: stretch;
  background: var(--hd-margin);
}
.spine.blank { background: var(--hd-feint); }

.who { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex-grow: 1; }
.name { font-size: var(--hd-t-name); line-height: 1.2; }
.meta { font-family: var(--hd-sans); font-size: var(--hd-t-figure); color: var(--hd-pencil); }

/* Baseline-aligned by hand: the mic is a block-level svg (HdIcon), so nothing
   else lines it up with the numerals sitting either side of it. */
.counts {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 5px;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-figure);
  color: var(--hd-pencil);
}
.counts .n { font-size: var(--hd-t-figure); font-variant-numeric: tabular-nums; color: var(--hd-ink); }
.counts .said { color: var(--hd-margin); }
.counts .mic { color: var(--hd-margin); margin-left: 2px; }
.counts .dot { color: var(--hd-feint); }
.counts.blank { font-style: italic; }

.add { margin-top: 14px; }
.add.first { margin-top: 20px; }

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 44px;
  padding: 0 4px 0 0;
  background: none;
  border: 0;
  color: var(--hd-margin);
  font-family: var(--hd-sans);
  font-weight: 600;
  font-size: var(--hd-t-control);
  cursor: pointer;
}

.add-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 26em;
  margin-top: 14px;
  padding: 16px;
  background: var(--hd-sheet);
}

.field { display: flex; flex-direction: column; gap: 4px; }
.field .label { font-family: var(--hd-sans); font-size: var(--hd-t-figure); color: var(--hd-pencil); }
.field input,
.field select {
  min-height: 44px;
  padding: 8px 10px;
  border: 1px solid var(--hd-feint);
  background: var(--hd-paper);
  color: var(--hd-ink);
  font-family: var(--hd-serif);
  font-size: var(--hd-t-body);
}

.actions { display: flex; justify-content: flex-end; gap: 20px; margin-top: 4px; }
.actions button {
  min-height: 44px;
  padding: 0 4px;
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-weight: 600;
  font-size: var(--hd-t-small);
  cursor: pointer;
}
.cancel { color: var(--hd-pencil); }
.save { color: var(--hd-margin); }
</style>
